import { SITE_URL } from '../seo/siteUrl';

const BASE_URL = SITE_URL;
const DEFAULT_IMAGE = '/icons/purdue_search_logo.png';

/**
 * Per-page SEO head tags. React 19 natively hoists <title>, <meta>, and
 * <link> elements rendered anywhere in the component tree into <head>,
 * so no react-helmet dependency is needed.
 *
 * @param {string}  title       - Page-specific title. Pass the full title
 *                                string for the home page (no suffix appended).
 * @param {string}  description - Meta description (≤160 chars recommended).
 * @param {string}  canonical   - Path only, e.g. "/about". Required.
 * @param {string}  [ogImage]     - Absolute URL or root-relative path to OG image.
 * @param {string}  [ogImageAlt]  - Alt text for the OG/Twitter image. Defaults to
 *                                  "Purdue SEARCH logo" (correct for the default logo image;
 *                                  override when passing a page-specific `ogImage`).
 * @param {boolean} [noindex]     - Set true for search/404/utility pages.
 * @param {boolean} [fullTitle]   - Set true when `title` is already the full
 *                                  document title (skips " | Purdue SEARCH" suffix).
 */
export default function SEOHead({
  title,
  description,
  canonical,
  ogImage = DEFAULT_IMAGE,
  ogImageAlt = 'Purdue SEARCH logo',
  noindex = false,
  fullTitle = false,
}) {
  const docTitle  = fullTitle ? title : `${title} | Purdue SEARCH`;
  const fullUrl   = `${BASE_URL}${canonical}`;
  const fullImage = ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`;
  // Default logo is a known 200x200px PNG (public/icons/purdue_search_logo.png);
  // only emit width/height when the default (unsized) image is in use, since a
  // caller-supplied ogImage may be a different size.
  const isDefaultImage = ogImage === DEFAULT_IMAGE;

  return (
    <>
      <title>{docTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type"        content="website" />
      <meta property="og:url"         content={fullUrl} />
      <meta property="og:title"       content={docTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image"       content={fullImage} />
      <meta property="og:image:alt"   content={ogImageAlt} />
      {isDefaultImage && <meta property="og:image:width"  content="200" />}
      {isDefaultImage && <meta property="og:image:height" content="200" />}

      {/* Twitter Card. X rejects summary_large_image below 300x157 and falls back
          to a text-only card, so the square 200x200 logo has to ship as "summary".
          Pages that supply their own (wide) ogImage — currently only BlogPost with
          a cover image — still get the large card. */}
      <meta name="twitter:card"        content={isDefaultImage ? 'summary' : 'summary_large_image'} />
      <meta name="twitter:site"        content="@purduesearch" />
      <meta name="twitter:title"       content={docTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={fullImage} />
      <meta name="twitter:image:alt"   content={ogImageAlt} />

      {/* hreflang — English-only site. The JSX prop must be camelCase
          `hrefLang`: React rejects a lowercase `hreflang` as an unknown DOM
          property and drops it, emitting a bare <link rel="alternate"> with no
          hreflang at all (it only warns in development). */}
      <link rel="alternate" hrefLang="en"        href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {noindex && <meta name="robots" content="noindex, follow" />}
    </>
  );
}
